import { mockUrlReplacer } from './mockUrlReplacer';

type UrlFn = (url?: URL) => unknown;

const createUrlService = () => {
  const urlCheckWrapper = <T extends UrlFn>(fn: T): T => {
    return ((url?: URL) => {
      if (!(url instanceof URL)) {
        return;
      }
      return fn(url);
    }) as T;
  };

  const isPathnameStartsWith = (path: string) => (url?: URL) =>
    url ? url.pathname.toLowerCase().startsWith(path) : false;

  const isHostnameEquals = (hostname: string) => {
    return (url?: URL) => {
      if (!url) {
        return false;
      }
      const realUrl = createEventUrl(url.href);
      return realUrl ? realUrl.hostname.toLowerCase() === hostname : false;
    };
  };

  const createEventUrl = (url?: string) => {
    if (typeof url === 'string' && url) {
      try {
        const replacedUrl = mockUrlReplacer.mockToReal(url);
        return new URL(decodeURIComponent(replacedUrl));
      } catch (e) {
        console.error(e);
        return undefined;
      }
    }
    return undefined;
  };

  const isUrlMatchesRegExp = (regExp: RegExp, url?: string) => {
    if (!url) {
      return false;
    }

    return regExp.test(url);
  };

  const urlRegex = /^(https?:\/\/)?(([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,})(\/\S*)?(\?\S*)?(#\S*)?$/;

  const isValidUrl = (url: string): boolean => urlRegex.test(url);

  const isLinkedinCompanyPage = isPathnameStartsWith('/company/');
  const isLinkedinCompanyPeoplePage = (url?: URL) => (url ? url.pathname.split('/')[3] === 'people' : false);
  const isLinkedinContactPage = isPathnameStartsWith('/in/');
  const isLinkedinSearchPage = isPathnameStartsWith('/search/results/people/');
  const isLinkedinSearchAllPage = isPathnameStartsWith('/search/results/all/');
  const isSalesNavContactPage = isPathnameStartsWith('/sales/lead/');
  const isSalesNavCompanyPage = isPathnameStartsWith('/sales/company/');
  const isSalesNavSearchPage = isPathnameStartsWith('/sales/search/people');
  const isSalesNavListPage = isPathnameStartsWith('/sales/lists/people/');
  const isSalesNavPage = isPathnameStartsWith('/sales/');

  const getLinkedinIdFromUrl = (url?: URL): string | undefined => {
    if (!url) {
      return undefined;
    }
    const [, , linkedinId] = url.pathname.split('/');
    return linkedinId && decodeURI(linkedinId);
  };

  const getContactLinkedinIdFromUrl = (url?: URL): string | undefined => {
    if (!url || !isLinkedinContactPage(url)) {
      return undefined;
    }
    return getLinkedinIdFromUrl(url);
  };

  const getCompanyLinkedinIdFromSalesNavPathname = (pathname: string): string | undefined => {
    const [, , , linkedinId] = pathname.split('/');
    if (pathname.includes('?')) {
      return linkedinId.split('?')[0];
    }
    return linkedinId;
  };

  const removeWWW = (url?: string) => (typeof url === 'string' ? url.replace(/^www\./, '') : undefined);

  const isAutoOpenLinkedinPage = (url?: URL) =>
    isLinkedinCompanyPage(url) ||
    isLinkedinContactPage(url) ||
    isLinkedinSearchPage(url) ||
    isLinkedinSearchAllPage(url) ||
    isSalesNavContactPage(url) ||
    isSalesNavCompanyPage(url) ||
    isSalesNavSearchPage(url) ||
    isSalesNavListPage(url);

  const getPageFromUrl = (url?: URL) => {
    const pageString = url ? url.searchParams.get('page') : undefined;
    const pageNumber = pageString ? parseInt(pageString, 10) : 1;
    return Number.isNaN(pageNumber) ? 1 : pageNumber;
  };

  const isGoogleCalendarUrl = (url?: URL) => isHostnameEquals('calendar.google.com')(url);
  const isOutlookCalendarUrl = (url?: URL) =>
    isHostnameEquals('outlook.live.com')(url) && isPathnameStartsWith('/calendar/')(url);
  const isCalendarUrl = (url?: URL) => isGoogleCalendarUrl(url) || isOutlookCalendarUrl(url);

  const isSalesloftUrl = (url?: URL) => isUrlMatchesRegExp(/app.salesloft.com$/, url?.hostname);
  const isSalesloftCompanyPage = (url?: URL) =>
    isUrlMatchesRegExp(/^https:\/\/app.salesloft.com\/app\/company\/\d+\/??.*/, url?.href);
  const isSalesloftContactPage = (url?: URL) =>
    isUrlMatchesRegExp(/^https:\/\/app.salesloft.com\/app\/people\/\d+\/??.*/, url?.href);

  const isHubspotUrl = (url?: URL) => isUrlMatchesRegExp(/^app-?\w*.hubspot\.com$/, url?.hostname);
  const isHubspotCompanyPage = (url?: URL) =>
    isUrlMatchesRegExp(
      /^https:\/\/app-?\w*\.hubspot.com\/contacts\/\d+\/record\/0-2\/\d+\/?(index\.html)?(\?.*)?$/,
      url?.href
    );
  const isHubspotContactPage = (url?: URL) =>
    isUrlMatchesRegExp(
      /^https:\/\/app-?\w*\.hubspot.com\/contacts\/\d+\/record\/0-1\/\d+\/?(index\.html)?(\?.*)?$/,
      url?.href
    );
  const isHubspotDealPage = (url?: URL) =>
    isUrlMatchesRegExp(
      /^https:\/\/app-?\w*\.hubspot.com\/contacts\/\d+\/record\/0-3\/\d+\/?(index\.html)?(\?.*)?$/,
      url?.href
    );

  const isMicrosoftDynamicsUrl = (url?: URL) => isUrlMatchesRegExp(/^.*\.crm\w+\.dynamics\.com$/, url?.hostname);
  const isMicrosoftDynamicsContactPage = (url?: URL) => !!url?.searchParams.get('etn')?.includes('contact');
  const isMicrosoftDynamicsCompanyPage = (url?: URL) => !!url?.searchParams.get('etn')?.includes('account');
  const isMicrosoftDynamicsLeadPage = (url?: URL) => !!url?.searchParams.get('etn')?.includes('lead');

  const isProPage = (url?: URL) => isHostnameEquals('pro.similarweb.com')(url);

  const isSalesforceLightningUrl = (url?: URL) => isUrlMatchesRegExp(/^.*\.force\.com$/, url?.hostname);
  const isSalesforceLightningContactPage = (url?: URL) =>
    !!url?.pathname.toLowerCase().includes('/lightning/r/contact/');
  const isSalesforceLightningAccountPage = (url?: URL) =>
    !!url?.pathname.toLowerCase().includes('/lightning/r/account/');
  const isSalesforceLightningLeadPage = (url?: URL) => !!url?.pathname.toLowerCase().includes('/lightning/r/lead/');

  const isSalesforceUrl = (url?: URL) => isUrlMatchesRegExp(/^.*\.salesforce\.com$/, url?.hostname);
  const isSalesforceContactPage = isPathnameStartsWith('/003');
  const isSalesforceAccountPage = isPathnameStartsWith('/001');
  const isSalesforceLeadPage = isPathnameStartsWith('/00q');

  const isGoogleSheetsUrl = (url?: URL) =>
    isHostnameEquals('docs.google.com') && isPathnameStartsWith('/spreadsheets')(url);

  const isGoogleLocalDevUrl = (url?: URL) =>
    url?.hostname === 'www.google.com' && url.searchParams.get('local') === '1';

  return {
    createEventUrl,
    isLinkedinCompanyPage: urlCheckWrapper(isLinkedinCompanyPage),
    isLinkedinCompanyPeoplePage: urlCheckWrapper(isLinkedinCompanyPeoplePage),
    isLinkedinContactPage: urlCheckWrapper(isLinkedinContactPage),
    isLinkedinSearchPage: urlCheckWrapper(isLinkedinSearchPage),
    isLinkedinSearchAllPage: urlCheckWrapper(isLinkedinSearchAllPage),
    isLinkedinUrl: urlCheckWrapper(isHostnameEquals('www.linkedin.com')),
    getCompanyLinkedinIdFromUrl: urlCheckWrapper(getLinkedinIdFromUrl),
    getContactLinkedinIdFromUrl: urlCheckWrapper(getContactLinkedinIdFromUrl),
    isSalesNavPage: urlCheckWrapper(isSalesNavPage),
    isSalesNavContactPage: urlCheckWrapper(isSalesNavContactPage),
    isSalesNavCompanyPage: urlCheckWrapper(isSalesNavCompanyPage),
    isSalesNavSearchPage: urlCheckWrapper(isSalesNavSearchPage),
    isSalesNavListPage: urlCheckWrapper(isSalesNavListPage),
    getCompanyLinkedinIdFromSalesNavPathname,
    removeWWW,
    isValidUrl,
    isAutoOpenLinkedinPage: urlCheckWrapper(isAutoOpenLinkedinPage),
    getPageFromUrl: urlCheckWrapper(getPageFromUrl),
    isGoogleCalendarUrl: urlCheckWrapper(isGoogleCalendarUrl),
    isOutlookCalendarUrl: urlCheckWrapper(isOutlookCalendarUrl),
    isCalendarUrl: urlCheckWrapper(isCalendarUrl),
    isSalesloftUrl: urlCheckWrapper(isSalesloftUrl),
    isSalesloftCompanyPage: urlCheckWrapper(isSalesloftCompanyPage),
    isSalesloftContactPage: urlCheckWrapper(isSalesloftContactPage),
    isHubspotUrl: urlCheckWrapper(isHubspotUrl),
    isHubspotCompanyPage: urlCheckWrapper(isHubspotCompanyPage),
    isHubspotContactPage: urlCheckWrapper(isHubspotContactPage),
    isHubspotDealPage: urlCheckWrapper(isHubspotDealPage),
    isMicrosoftDynamicsUrl: urlCheckWrapper(isMicrosoftDynamicsUrl),
    isMicrosoftDynamicsCompanyPage: urlCheckWrapper(isMicrosoftDynamicsCompanyPage),
    isMicrosoftDynamicsContactPage: urlCheckWrapper(isMicrosoftDynamicsContactPage),
    isMicrosoftDynamicsLeadPage: urlCheckWrapper(isMicrosoftDynamicsLeadPage),
    isGmailUrl: urlCheckWrapper(isHostnameEquals('mail.google.com')),
    isProPage: urlCheckWrapper(isProPage),
    isSalesforceLightningUrl: urlCheckWrapper(isSalesforceLightningUrl),
    isSalesforceLightningContactPage: urlCheckWrapper(isSalesforceLightningContactPage),
    isSalesforceLightningAccountPage: urlCheckWrapper(isSalesforceLightningAccountPage),
    isSalesforceLightningLeadPage: urlCheckWrapper(isSalesforceLightningLeadPage),
    isSalesforceUrl: urlCheckWrapper(isSalesforceUrl),
    isSalesforceContactPage: urlCheckWrapper(isSalesforceContactPage),
    isSalesforceAccountPage: urlCheckWrapper(isSalesforceAccountPage),
    isSalesforceLeadPage: urlCheckWrapper(isSalesforceLeadPage),
    isGoogleSheetsUrl: urlCheckWrapper(isGoogleSheetsUrl),
    isGoogleLocalDevUrl: urlCheckWrapper(isGoogleLocalDevUrl),
  };
};

const UrlService = createUrlService();

export default UrlService;
